import * as Sentry from '@sentry/react';

/**
 * Report a failed `commands.*` call to Sentry.
 *
 * The generated bindings reject with serde-tagged plain objects, never with
 * `Error`. Passing those straight to `captureException` produced issues titled
 * "Object captured as exception" with no message and no stack, which is what
 * made LUMEN-4R unreadable and ungroupable. Wrapping them gives every issue a
 * searchable title built from the command and the variant, while the original
 * payload is preserved as context.
 */

/**
 * Variants that mean "the recording already ended". Stop and discard are
 * documented best-effort on the Rust side, and an auto-stop tier routinely
 * wins the race with the user's click, so this is an expected outcome rather
 * than a fault worth alerting on.
 */
const BENIGN_KINDS = new Set(['not_found']);

function kindOf(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const kind = (error as Record<string, unknown>).kind;
  return typeof kind === 'string' ? kind : null;
}

export function reportCommandError(command: string, error: unknown): void {
  const kind = kindOf(error);
  if (kind !== null && BENIGN_KINDS.has(kind)) return;

  const context = { extra: { command, error } };

  if (error instanceof Error) {
    Sentry.captureException(error, context);
    return;
  }

  const detail = kind ?? 'unknown error';
  Sentry.captureException(new Error(`${command} failed: ${detail}`), context);
}
